// @ts-nocheck
import '@geoman-io/leaflet-geoman-free';
import { useLeafletContext } from '@react-leaflet/core';
import L from 'leaflet';
import 'leaflet-toolbar';
import { useCallback, useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Eye, EyeOff } from 'react-feather';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { startUpdatingProfileMapOptions } from '../../../../../redux/actions/auth';
import * as authSelectors from '../../../../../redux/reducers/auth';

// Global registry for toolbar toggle functions and store access
const toolbarRegistry = new Map();
let globalStore = null;

export interface UnitLabelsToolbarOptions {
  useToolbar: boolean;
  position?: 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
  listenMapControlChanges?: any[];
  // New props for local state management
  useLocalState?: boolean;
  localLabelsVisible?: boolean;
  onLocalToggle?: (visible: boolean) => void;
}

/* -------------------------------------------------------------------------- */
/*                         ToolbarUnitLabelsControl                          */
/* -------------------------------------------------------------------------- */

const ToolbarUnitLabelsControl = (props: UnitLabelsToolbarOptions) => {
  const {
    useToolbar,
    position = 'topleft',
    listenMapControlChanges,
    useLocalState = false,
    localLabelsVisible = false,
    onLocalToggle
  } = props;

  const context = useLeafletContext();
  const intl = useIntl();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => ({
    user: authSelectors.getAuthUser(state)
  }));

  // Determine which state to use based on useLocalState prop
  const isUnitLabelsVisible = useLocalState
    ? localLabelsVisible
    : user?.unitMapLabelAlwaysOn === 1 || user?.unitMapLabelAlwaysOn === '1';

  // Generate unique ID for this toolbar instance
  const toolbarId = useRef(`unit-labels-toolbar-${Date.now()}-${Math.random()}`);

  // Create toggle function that always has access to current state
  const toggleUnitLabels = useCallback(() => {
    if (useLocalState && onLocalToggle) {
      // Use local state toggle
      console.log('ToolbarUnitLabelsControl - Toggling unit labels (LOCAL):', {
        currentValue: localLabelsVisible,
        newValue: !localLabelsVisible
      });
      onLocalToggle(!localLabelsVisible);
    } else {
      // Use original profile update behavior
      const currentValue = user?.unitMapLabelAlwaysOn === 1 || user?.unitMapLabelAlwaysOn === '1';
      const newValue = !currentValue;

      console.log('ToolbarUnitLabelsControl - Toggling unit labels (PROFILE):', {
        currentValue,
        newValue,
        user: user?.unitMapLabelAlwaysOn,
        userType: typeof user?.unitMapLabelAlwaysOn
      });

      // Send the complete map options payload like the working profile switch does
      dispatch(
        startUpdatingProfileMapOptions({
          enableLocationClustering:
            user?.enableLocationClustering === 1 || user?.enableLocationClustering === '1',
          locationMapLabelAlwaysOn:
            user?.locationMapLabelAlwaysOn === 1 || user?.locationMapLabelAlwaysOn === '1',
          activateUnitTrackMode:
            user?.activateUnitTrackMode === 1 || user?.activateUnitTrackMode === '1',
          unitMapLabelAlwaysOn: newValue, // The boolean value we're toggling
          unitsLabelContent: user?.unitsLabelContent,
          zoomLevelToStartClustering: Number(user?.zoomLevelToStartClustering || 13),
          countriesForGoogleSearch: user?.countriesForGoogleSearch,
          unitMarkersRotation: user?.unitMarkersRotation === 1 || user?.unitMarkersRotation === '1',
          unitTrajectory: user?.unitTrajectory === 1 || user?.unitTrajectory === '1'
        })
      );
    }
  }, [useLocalState, localLabelsVisible, onLocalToggle, user, dispatch]);

  // Register the toggle function
  useEffect(() => {
    toolbarRegistry.set(toolbarId.current, toggleUnitLabels);

    return () => {
      toolbarRegistry.delete(toolbarId.current);
    };
  }, [toggleUnitLabels]);

  useEffect(() => {
    const container = context.layerContainer || context.map;
    let unitLabelsToolbar;

    try {
      // Get the current toolbar ID
      const currentToolbarId = toolbarId.current;

      // Definimos la acción del toolbar con el estado actual
      var unitLabelsAction = L.Toolbar2.Action.extend({
        options: {
          toolbarId: currentToolbarId,
          toolbarIcon: {
            tooltip: isUnitLabelsVisible
              ? intl.formatMessage(
                  { id: 'map.toolbar.hideUnitLabels' },
                  { defaultMessage: 'Hide unit labels' }
                )
              : intl.formatMessage(
                  { id: 'map.toolbar.showUnitLabels' },
                  { defaultMessage: 'Show unit labels always' }
                ),
            html: ReactDOMServer.renderToStaticMarkup(
              <>{isUnitLabelsVisible ? <EyeOff size={14} /> : <Eye size={14} />}</>
            )
          }
        },
        addHooks: function () {
          // Get the toggle function from the registry
          const toggleFunction = toolbarRegistry.get(this.options.toolbarId);
          if (toggleFunction) {
            toggleFunction();
          }
        },
        disable: function (e) {
          L.Toolbar2.Action.prototype.disable.call(this);
        }
      });

      // Creamos el toolbar con la nueva acción
      L.Toolbar2.UnitLabelsToolbar = L.Toolbar2.Control.extend({
        options: {
          actions: [unitLabelsAction]
        }
      });

      unitLabelsToolbar = new L.Toolbar2.UnitLabelsToolbar({ position: position });
      unitLabelsToolbar.addTo(container);

      return () => {
        if (unitLabelsToolbar) {
          unitLabelsToolbar.remove();
        }
      };
    } catch (error) {
      console.error('Error creating unit labels toolbar:', error);
    }
  }, [...listenMapControlChanges, isUnitLabelsVisible]);

  return null;
};

ToolbarUnitLabelsControl.defaultProps = {
  position: 'topleft',
  useToolbar: false,
  listenMapControlChanges: []
};

export default ToolbarUnitLabelsControl;
